/**
 * How dates are shown and typed: day first (18/10/2026) for most of the world including India, month first (10/18/2026) for the
 * United States and the Philippines, year first (2026-10-18) for China, Japan, Korea and a few others. It follows the organization's
 * country, and an organization can choose its own (organizations.date_format). Dates are kept and sent as YYYY-MM-DD; this is only
 * how they look. A document that carries a date names the month in words (18 Oct 2026) where it can, which every country reads alike.
 */
export const DATE_FORMATS = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'];
export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';

const MONTH_FIRST = ['united states', 'united states of america', 'usa', 'us', 'philippines', 'micronesia', 'palau', 'marshall islands'];
const YEAR_FIRST = ['china', 'japan', 'south korea', 'korea', 'north korea', 'taiwan', 'hungary', 'lithuania', 'sweden', 'iran', 'mongolia'];

const STORAGE_KEY = 'dateFormat';

/** The usual format of a country, given by name (India) or code (IN); day first when unknown. */
export function defaultDateFormat(country) {
  const c = String(country || '').trim().toLowerCase();
  if (MONTH_FIRST.includes(c)) return 'MM/DD/YYYY';
  if (YEAR_FIRST.includes(c)) return 'YYYY-MM-DD';
  return DEFAULT_DATE_FORMAT;
}

/** The organization's own choice when it has made one, else its country's usual format. */
export function resolveDateFormat(override, country) {
  return DATE_FORMATS.includes(override) ? override : defaultDateFormat(country);
}

let active = (() => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return DATE_FORMATS.includes(stored) ? stored : DEFAULT_DATE_FORMAT;
  } catch (e) {
    return DEFAULT_DATE_FORMAT;
  }
})();

/** The format in use now (the signed-in organization's). */
export const getActiveDateFormat = () => active;

/** Sets the format in use, and remembers it so the next page load starts with it. */
export function setActiveDateFormat(format) {
  active = DATE_FORMATS.includes(format) ? format : DEFAULT_DATE_FORMAT;
  try {
    localStorage.setItem(STORAGE_KEY, active);
  } catch (e) {
    // Remembering it only saves a flicker on the next load
  }
}

const pad = (n) => String(n).padStart(2, '0');

function layout(year, month, day, format) {
  if (format === 'MM/DD/YYYY') return `${pad(month)}/${pad(day)}/${year}`;
  if (format === 'YYYY-MM-DD') return `${year}-${pad(month)}-${pad(day)}`;
  return `${pad(day)}/${pad(month)}/${year}`;
}

/** The year, month and day of a date given as YYYY-MM-DD, a date and time, or a Date, as the person's own calendar sees them. */
function parts(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const [y, m, d] = value.trim().split('-').map(Number);
    return { y, m, d };
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return { y: date.getFullYear(), m: date.getMonth() + 1, d: date.getDate() };
}

/** A date for display in the organization's format; '' when there is none. */
export function formatDate(value, format = active) {
  const p = parts(value);
  return p ? layout(p.y, p.m, p.d, format) : '';
}

/** A date and time for display: the date in the organization's format, the time as the device shows it. */
export function formatDateTime(value, format = active) {
  if (value == null || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${formatDate(date, format)} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

/** What to type into an empty date box: dd/mm/yyyy. */
export function datePlaceholder(format = active) {
  return format.replace('DD', 'dd').replace('MM', 'mm').replace('YYYY', 'yyyy');
}

/**
 * What was typed, read as a date in the format and returned as YYYY-MM-DD; '' when it is not a real date (31/02/2026) or incomplete.
 * Slashes, dashes, dots and spaces all separate; a two-digit year is the year in this century.
 */
export function parseDate(text, format = active) {
  const t = String(text || '').trim();
  if (!t) return '';
  const bits = t.split(/[\s/.-]+/).filter(Boolean);
  if (bits.length !== 3 || bits.some((b) => !/^\d+$/.test(b))) return '';
  let day;
  let month;
  let year;
  if (format === 'MM/DD/YYYY') [month, day, year] = bits;
  else if (format === 'YYYY-MM-DD') [year, month, day] = bits;
  else [day, month, year] = bits;
  if (year.length === 2) year = `20${year}`;
  if (year.length !== 4) return '';
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  const check = new Date(y, m - 1, d);
  if (check.getFullYear() !== y || check.getMonth() !== m - 1 || check.getDate() !== d) return '';
  return `${year}-${pad(m)}-${pad(d)}`;
}

/** Digits typed into a date box with the separators put in as you go: 1810 becomes 18/10/ for day first. */
export function maskDateInput(text, format = active) {
  const digits = String(text || '').replace(/\D/g, '').slice(0, 8);
  const sep = format === 'YYYY-MM-DD' ? '-' : '/';
  const cuts = format === 'YYYY-MM-DD' ? [4, 6] : [2, 4];
  let out = '';
  for (let i = 0; i < digits.length; i += 1) {
    if (cuts.includes(i)) out += sep;
    out += digits[i];
  }
  return out;
}
